/* Серверные истории: REST даёт снимок и мутации, WebSocket — изменения в реальном времени. */

import { STORY_LIFETIME_MS, type StoryItem, type User, type UserStories } from '@andresot/protocol';
import type { ServerMessage } from '@andresot/protocol/envelope';
import type { StoryCreate } from '@andresot/protocol/stories';
import * as api from '$api/stories';
import { socket } from '$api/socket';

export const DEFAULT_STORY_DURATION = 5000;

export interface StoryRow {
	userId: string;
	items: StoryItem[];
	hasUnseen: boolean;
	isMine: boolean;
}

interface OpenState {
	userId: string;
	index: number;
}

class StoriesState {
	private byUser = $state<Record<string, UserStories>>({});
	private myId = '';
	private lifecycle = 0;
	private unsubscribe: (() => void) | null = null;
	private unsubscribePhase: (() => void) | null = null;
	/** Связь уже была открыта: следующее «open» означает, что она рвалась. */
	private socketOpened = false;
	/*
	 * Пока идёт запрос ленты, события копятся здесь. `null` — запроса нет.
	 * Без этого событие, пришедшее в середине запроса, затиралось ответом,
	 * собранным до него.
	 */
	private buffered: ServerMessage[] | null = null;
	/** Идущая загрузка ленты и просьба повторить её, пришедшая за это время. */
	private loadingSnapshot: { promise: Promise<void> } | null = null;
	private snapshotStale = false;
	private clock: ReturnType<typeof setInterval> | null = null;
	private pendingViews = new Set<string>();

	open = $state<OpenState | null>(null);
	paused = $state(false);
	composerOpen = $state(false);
	loading = $state(false);
	viewersByStory = $state<Record<string, User[]>>({});
	/** Авторы, чью ленту зритель попросил не показывать. */
	hiddenAuthorIds = $state<Set<string>>(new Set());
	private now = $state(Date.now());

	rows = $derived.by(() => {
		const rows: StoryRow[] = Object.values(this.byUser)
			.map((entry) => {
				const items = entry.items.filter(
					(item) => item.expiresAt > this.now && this.now - item.createdAt < STORY_LIFETIME_MS
				);
				return {
					userId: entry.userId,
					items,
					hasUnseen: items.some((item) => !item.viewedByMe),
					isMine: entry.userId === this.myId
				};
			})
			// Скрытый автор не должен вернуться в ленту живым событием: сервер
			// его не пришлёт, а вот сообщение о новой истории — да.
			.filter((row) => row.items.length > 0 && !this.hiddenAuthorIds.has(row.userId));
		return rows.sort((a, b) => {
			if (a.isMine !== b.isMine) return a.isMine ? -1 : 1;
			if (a.hasUnseen !== b.hasUnseen) return a.hasUnseen ? -1 : 1;
			return (b.items.at(-1)?.createdAt ?? 0) - (a.items.at(-1)?.createdAt ?? 0);
		});
	});

	hasMine = $derived(this.rows.some((row) => row.isMine));

	current = $derived.by(() => {
		if (!this.open) return null;
		const row = this.rows.find((item) => item.userId === this.open!.userId);
		if (!row) return null;
		return { row, item: row.items[this.open.index] ?? null, index: this.open.index };
	});

	/**
	 * Скрыть ленту автора или вернуть её.
	 *
	 * После возврата лента перечитывается целиком: пока автор был скрыт,
	 * сервер его историй не присылал, и локально их просто нет.
	 */
	async setAuthorHidden(userId: string, hidden: boolean): Promise<void> {
		const lifecycle = this.lifecycle;
		try {
			if (hidden) await api.hideStoryAuthor(userId);
			else await api.showStoryAuthor(userId);
			if (lifecycle === this.lifecycle) await this.loadSnapshot();
		} catch (error) {
			if (lifecycle === this.lifecycle) throw error;
		}
	}

	/**
	 * Взять ленту с сервера целиком.
	 *
	 * Ответ подставляется вместо прежней ленты, а не сливается с ней: пока
	 * связи не было, историю могли и удалить, и слияние вернуло бы удалённое.
	 * Всё, что пришло событием во время запроса, ждёт в буфере и применяется
	 * следом — иначе ответ, собранный на сервере раньше события, затирал бы его.
	 */
	private loadSnapshot(): Promise<void> {
		/*
		 * Один запрос за раз. Второй, начатый поверх первого (связь вернулась,
		 * пока лента ещё грузилась), делил с ним один буфер: первый ответ забирал
		 * и обнулял его, второй перебирал уже пустоту и падал, а события,
		 * пришедшие между ответами, применялись к ленте, которую тут же затирал
		 * более старый снимок.
		 *
		 * Просьба, пришедшая во время запроса, не теряется: идущий ответ мог быть
		 * собран до того, ради чего просят, поэтому следом идёт ещё один.
		 */
		if (this.loadingSnapshot) {
			this.snapshotStale = true;
			return this.loadingSnapshot.promise;
		}

		const lifecycle = this.lifecycle;
		const run = { promise: null as unknown as Promise<void> };
		run.promise = (async () => {
			try {
				do {
					this.snapshotStale = false;
					await this.fetchSnapshot();
				} while (lifecycle === this.lifecycle && this.snapshotStale);
			} catch (error) {
				if (lifecycle === this.lifecycle) throw error;
			} finally {
				// В том же такте, что и выход из цикла: просьба, пришедшая позже,
				// уже начнёт новую загрузку, а не повиснет на законченной.
				if (this.loadingSnapshot === run) this.loadingSnapshot = null;
			}
		})();
		this.loadingSnapshot = run;
		return run.promise;
	}

	private async fetchSnapshot(): Promise<void> {
		const lifecycle = this.lifecycle;
		// Буфер свой у каждого запроса: чужой его не обнулит.
		const buffer: ServerMessage[] = [];
		this.buffered = buffer;
		try {
			const { stories: rows, hiddenAuthorIds } = await api.fetchStories();
			if (lifecycle !== this.lifecycle) return;
			this.hiddenAuthorIds = new Set(hiddenAuthorIds);
			this.byUser = Object.fromEntries(rows.map((entry) => [entry.userId, entry]));
			// Буфер закрываем до применения: иначе события легли бы обратно в него же.
			this.buffered = null;
			for (const event of buffer) this.applyEvent(event);
		} finally {
			if (this.buffered === buffer) this.buffered = null;
		}
	}

	async start(myId: string): Promise<void> {
		this.stop();
		const lifecycle = ++this.lifecycle;
		this.myId = myId;
		this.loading = true;
		this.unsubscribe = socket.onMessage((event) => this.applyEvent(event));

		/*
		 * Сверка после обрыва связи.
		 *
		 * Событие о новой истории уходит только тем, кто подключён в эту
		 * секунду. Телефон усыпляет вкладку постоянно: событие терялось, и
		 * чужая история не появлялась до перезапуска приложения. У чатов такая
		 * сверка есть давно, у ленты её не было.
		 *
		 * Признак берётся из нынешней фазы: подписка ставится после запуска
		 * чатов, связь к этому времени обычно уже открыта, и ждать первого
		 * «open» значило бы проглотить первое же переподключение.
		 */
		this.socketOpened = socket.phase === 'open';
		this.unsubscribePhase = socket.onPhase((phase) => {
			if (phase !== 'open') return;
			if (!this.socketOpened) {
				this.socketOpened = true;
				return;
			}
			// Не дошло — повторит следующее переподключение; ронять страницу
			// необработанным отказом из-за ленты историй незачем.
			void this.loadSnapshot().catch(() => undefined);
		});

		this.clock = setInterval(() => (this.now = Date.now()), 60_000);
		try {
			await this.loadSnapshot();
		} finally {
			if (lifecycle === this.lifecycle) this.loading = false;
		}
	}

	stop(): void {
		this.lifecycle += 1;
		this.unsubscribe?.();
		this.unsubscribePhase?.();
		this.unsubscribe = null;
		this.unsubscribePhase = null;
		this.socketOpened = false;
		this.buffered = null;
		if (this.clock) clearInterval(this.clock);
		this.clock = null;
		this.pendingViews.clear();
		this.loadingSnapshot = null;
		this.snapshotStale = false;
		this.loading = false;
		this.hiddenAuthorIds = new Set();
		this.byUser = {};
		this.viewersByStory = {};
		this.open = null;
		this.composerOpen = false;
		this.myId = '';
	}

	private applyEvent(event: ServerMessage): void {
		// Через сокет идёт весь поток приложения; в буфер ленты кладём только
		// её события, иначе он копил бы чужие сообщения на время запроса.
		if (event.op !== 'story.created' && event.op !== 'story.viewed' && event.op !== 'story.deleted')
			return;
		if (this.buffered) {
			this.buffered.push(event);
			return;
		}
		switch (event.op) {
			case 'story.created':
				this.upsert(event.payload.userId, event.payload.story);
				return;
			case 'story.viewed': {
				const story = this.find(event.payload.storyId);
				if (story) story.viewCount = event.payload.viewCount;
				return;
			}
			case 'story.deleted':
				this.remove(event.payload.userId, event.payload.storyId);
				return;
		}
	}

	private find(storyId: string): StoryItem | undefined {
		return Object.values(this.byUser)
			.flatMap((entry) => entry.items)
			.find((story) => story.id === storyId);
	}

	private upsert(userId: string, story: StoryItem): void {
		const entry = this.byUser[userId] ?? { userId, items: [] };
		const index = entry.items.findIndex((item) => item.id === story.id);
		if (index >= 0) entry.items[index] = story;
		else entry.items = [...entry.items, story].sort((a, b) => a.createdAt - b.createdAt);
		this.byUser[userId] = entry;
	}

	private remove(userId: string, storyId: string): void {
		const entry = this.byUser[userId];
		if (!entry) return;
		entry.items = entry.items.filter((item) => item.id !== storyId);
		delete this.viewersByStory[storyId];
		if (entry.items.length === 0) delete this.byUser[userId];
		if (this.current?.item?.id === storyId) this.close();
	}

	openStories(userId: string, index?: number): void {
		const row = this.rows.find((item) => item.userId === userId);
		if (!row) return;
		const firstUnseen = row.items.findIndex((item) => !item.viewedByMe);
		const start = index ?? (firstUnseen >= 0 ? firstUnseen : 0);
		this.open = { userId, index: Math.max(0, Math.min(start, row.items.length - 1)) };
		this.paused = false;
		void this.markSeen();
	}

	close(): void {
		this.open = null;
		this.paused = false;
	}

	next(): void {
		if (!this.open) return;
		const row = this.rows.find((item) => item.userId === this.open!.userId);
		if (!row) return this.close();
		if (this.open.index < row.items.length - 1) {
			this.open = { userId: this.open.userId, index: this.open.index + 1 };
			void this.markSeen();
			return;
		}
		const position = this.rows.findIndex((item) => item.userId === this.open!.userId);
		const nextRow = this.rows[position + 1];
		if (!nextRow) return this.close();
		this.open = { userId: nextRow.userId, index: 0 };
		void this.markSeen();
	}

	prev(): void {
		if (!this.open) return;
		if (this.open.index > 0) {
			this.open = { userId: this.open.userId, index: this.open.index - 1 };
			return;
		}
		const position = this.rows.findIndex((item) => item.userId === this.open!.userId);
		const previous = this.rows[position - 1];
		if (previous) this.open = { userId: previous.userId, index: previous.items.length - 1 };
	}

	private async markSeen(): Promise<void> {
		const lifecycle = this.lifecycle;
		const current = this.current;
		if (!current?.item || current.row.isMine || current.item.viewedByMe) return;
		if (this.pendingViews.has(current.item.id)) return;
		this.pendingViews.add(current.item.id);
		try {
			const updated = await api.viewStory(current.item.id);
			if (lifecycle !== this.lifecycle) return;
			this.upsert(current.row.userId, updated);
		} catch {
			// Сбой сети не закрывает просмотр: следующая сессия повторит отметку на сервере.
		} finally {
			if (lifecycle === this.lifecycle) this.pendingViews.delete(current.item.id);
		}
	}

	/** Публикует историю любого вида: composer собирает запрос по договору. */
	async publish(input: StoryCreate): Promise<void> {
		const lifecycle = this.lifecycle;
		const myId = this.myId;
		try {
			const story = await api.createStory(input);
			if (lifecycle !== this.lifecycle) return;
			this.upsert(myId, story);
			this.composerOpen = false;
		} catch (error) {
			if (lifecycle === this.lifecycle) throw error;
		}
	}

	async deleteMine(storyId: string): Promise<void> {
		const lifecycle = this.lifecycle;
		const myId = this.myId;
		try {
			await api.deleteStory(storyId);
			if (lifecycle === this.lifecycle) this.remove(myId, storyId);
		} catch (error) {
			if (lifecycle === this.lifecycle) throw error;
		}
	}

	async loadViewers(storyId: string): Promise<User[]> {
		const lifecycle = this.lifecycle;
		try {
			const viewers = await api.fetchStoryViewers(storyId);
			if (lifecycle !== this.lifecycle) return [];
			this.viewersByStory[storyId] = viewers;
			return viewers;
		} catch (error) {
			if (lifecycle === this.lifecycle) throw error;
			return [];
		}
	}
}

export const stories = new StoriesState();
